/**
 * Visits panel: the small, quiet "Probing" pill. Founder feedback
 * 2026-10-14 ("the feed looks like a red alert"): shown ONLY on rows with
 * meaningful probing (`probeAlert`, ADR-0244: a sender with no registry
 * name whose probe requests are 5+ and 25%+ of its visits, or a registry-
 * named row with 3+ universal probe requests). Amber outline, muted
 * label, no coral, and the row keeps its normal edge and line 2.
 */

import { TriangleAlert } from 'lucide-react';

import { Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

/**
 * The plain-words explanation of the quiet "Probing" pill — one sentence a
 * merchant reads on hover/focus/tap. Keeps the same wording the detail
 * header already uses for the probing line ("requests to sensitive paths")
 * so the row + detail say the same thing.
 */
export const PROBING_PILL_TOOLTIP =
  'This sender asked for sensitive paths like /.env or /wp-login.php. '
  + "That's often reconnaissance for a later attack — not a real page view.";

/** The quiet "Probing" pill with a tooltip that explains it in plain words. */
export function ProbingPill(): JSX.Element {
  const C = useC();
  return (
    <Tooltip content={PROBING_PILL_TOOLTIP} side="bottom" aria-label={`Probing: ${PROBING_PILL_TOOLTIP}`}>
      <span
        data-testid="visit-probing-pill"
        className="inline-flex items-center gap-1"
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: C.muted,
          border: `1px solid ${C.amberFg}`,
          borderRadius: 2,
          padding: '1px 5px',
          lineHeight: 1.4,
          flexShrink: 0,
        }}
      >
        <TriangleAlert size={10} color={C.amberFg} aria-hidden="true" />
        Probing
      </span>
    </Tooltip>
  );
}

/**
 * Visitor-supplied probe paths, each quoted and in monospace so they never
 * read as part of Sill's sentence (security review L3). Paths arrive
 * already cleaned by `displayProbePath`; React renders them as text.
 */
export function ProbeExamples({ examples }: { examples: readonly string[] }): JSX.Element {
  const C = useC();
  return (
    <>
      {examples.map((p, i) => (
        <span key={i}>
          {i > 0 ? ', ' : null}
          <code
            data-testid="probe-example"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.92em',
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 2,
              padding: '0 3px',
              overflowWrap: 'anywhere',
            }}
          >
            “{p}”
          </code>
        </span>
      ))}
    </>
  );
}
